// src/services/memberAvailabilityService.js
// Service for managing member unavailability date ranges (member_unavailability table).

import { getSupabaseClient } from "./supabaseClient.js";

/**
 * List unavailability blocks for a single team_member, ordered by start date.
 * Returns array of { id, member_id, date_from, date_to, reason, created_at }
 */
export async function listUnavailabilityForMember(memberId) {
    if (!memberId) return [];
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
        .from("member_unavailability")
        .select("id, member_id, date_from, date_to, reason, created_at")
        .eq("member_id", memberId)
        .order("date_from", { ascending: true });

    if (error) throw error;
    return data || [];
}

/**
 * List all unavailability blocks for a team, joined with the member's display name.
 * Returns array of { id, member_id, member_name, date_from, date_to, reason, created_at }
 */
export async function listUnavailabilityForTeam(teamId) {
    if (!teamId) return [];
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
        .from("member_unavailability")
        .select("id, member_id, date_from, date_to, reason, created_at, team_members(display_name)")
        .eq("team_id", teamId)
        .order("date_from", { ascending: true });

    if (error) throw error;
    return (data || []).map(r => ({
        id: r.id,
        member_id: r.member_id,
        member_name: r.team_members?.display_name || "—",
        date_from: r.date_from,
        date_to: r.date_to,
        reason: r.reason,
        created_at: r.created_at,
    }));
}

/**
 * Create a new unavailability block.
 * payload: { team_id, member_id, date_from, date_to, reason }
 */
export async function createUnavailability(payload) {
    if (!payload || typeof payload !== "object") throw new Error("payload inválido.");
    if (!payload.team_id) throw new Error("payload.team_id obligatorio.");
    if (!payload.member_id) throw new Error("payload.member_id obligatorio.");
    if (!payload.date_from || !payload.date_to) throw new Error("date_from y date_to son obligatorios.");

    const supabase = getSupabaseClient();
    const { data: { user } } = await supabase.auth.getUser();

    const { data, error } = await supabase
        .from("member_unavailability")
        .insert({
            team_id: payload.team_id,
            member_id: payload.member_id,
            date_from: payload.date_from,
            date_to: payload.date_to,
            reason: payload.reason || null,
            created_by: user?.id || null,
        })
        .select("id, member_id, date_from, date_to, reason, created_at")
        .single();

    if (error) throw error;
    return data;
}

/**
 * Delete an unavailability block by id.
 */
export async function deleteUnavailability(id) {
    if (!id) throw new Error("id es obligatorio.");
    const supabase = getSupabaseClient();
    const { error } = await supabase
        .from("member_unavailability")
        .delete()
        .eq("id", id);

    if (error) throw error;
    return true;
}
